import React, { useState } from 'react';
import { Copy, Facebook, Instagram, Music2, ImageDown, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { shareAffirmationImage } from '../utils/shareAffirmationImage';

// Instagram brand icon isn't in lucide-react — Instagram works, so we use it. TikTok
// isn't in lucide either; Music2 is the closest fit and looks tasteful in cream.

const AffirmationShareMenu = ({ theme, affirmation, index, image, eyebrow, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [savingImage, setSavingImage] = useState(false);

  // Public share URL — routes through backend so social crawlers (Facebook,
  // Twitter, iMessage, Slack, WhatsApp, Discord) get rich OG unfurls.
  // Regular browsers hit the same URL and are meta-refreshed into the SPA
  // at `/a/:themeId/:index`.
  const link = `${window.location.origin}/api/og/${theme.id}/${index}`;
  const shareText = `“${affirmation.title}” — ${affirmation.body.split('. ')[0]}. · Reflections by Rowdy`;

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied', { description: 'Paste it anywhere — friends can open the affirmation directly.' });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — long-press the link to copy manually.');
    }
  };

  const doFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=680,height=560');
  };

  // IG and TikTok have NO web share URL. Best UX: copy link + surface the app.
  const doInstagram = async () => {
    try { await navigator.clipboard.writeText(link); } catch {}
    toast.success('Link copied — opening Instagram', {
      description: 'Paste it into your Story caption, bio, or DM.',
      duration: 4200,
    });
    // Try to open Instagram app (mobile). Desktop falls back to instagram.com.
    setTimeout(() => {
      window.location.href = /iPhone|iPad|Android/i.test(navigator.userAgent)
        ? 'instagram://'
        : 'https://www.instagram.com/';
    }, 250);
  };

  const doTiktok = async () => {
    try { await navigator.clipboard.writeText(link); } catch {}
    toast.success('Link copied — opening TikTok', {
      description: 'Paste it into your caption or bio when you post.',
      duration: 4200,
    });
    setTimeout(() => {
      window.location.href = /iPhone|iPad|Android/i.test(navigator.userAgent)
        ? 'snssdk1233://'
        : 'https://www.tiktok.com/';
    }, 250);
  };

  const doSaveImage = async () => {
    if (savingImage) return;
    setSavingImage(true);
    try {
      const res = await shareAffirmationImage({ theme, affirmation, image, eyebrow });
      if (res.method === 'download') {
        toast.success('Story image saved', {
          description: 'Ready to post to Instagram Stories, Reels, or anywhere else.',
        });
      }
    } catch (err) {
      toast.error('Could not create image', { description: (err?.message || '').slice(0, 100) });
    } finally {
      setSavingImage(false);
    }
  };

  const buttons = [
    { key: 'copy',      icon: copied ? Check : Copy,  label: copied ? 'Copied' : 'Copy link',        onClick: doCopy,       tint: '#D6AD60' },
    { key: 'image',     icon: ImageDown,              label: savingImage ? 'Saving…' : 'Save image', onClick: doSaveImage,  tint: '#AA8347', disabled: savingImage },
    { key: 'facebook',  icon: Facebook,               label: 'Facebook',                             onClick: doFacebook,   tint: '#1e3a5f' },
    { key: 'instagram', icon: Instagram,              label: 'Instagram',                            onClick: doInstagram,  tint: '#7a9b8e' },
    { key: 'tiktok',    icon: Music2,                 label: 'TikTok',                               onClick: doTiktok,     tint: '#0f1e33' },
  ];

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="affirmation-share-menu"
    >
      <div className="absolute inset-0 bg-rbr-blue-deep/60 backdrop-blur-sm animate-fade-in" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[560px] mx-auto rounded-t-3xl sm:rounded-3xl bg-rbr-cream shadow-rbr-lift p-6 pb-8 animate-fade-up"
      >
        {/* Handle */}
        <div className="w-10 h-1 rounded-full bg-rbr-gold-soft mx-auto mb-4 sm:hidden" />

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-rbr-cream-warm hover:bg-rbr-gold-soft/50 flex items-center justify-center text-rbr-blue-deep transition-colors"
          aria-label="Close"
          data-testid="share-menu-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <p className="rbr-eyebrow mb-2">Share this affirmation</p>
        <h3
          className="rbr-heading text-2xl leading-tight mb-1"
          style={{ color: theme.gradient[1] }}
        >
          {affirmation.title}
        </h3>
        <p className="rbr-serif-italic text-rbr-sage-deep text-sm leading-relaxed mb-5">
          Copy the link, or send it straight to Instagram, Facebook, or TikTok.
        </p>

        {/* Link preview */}
        <button
          onClick={doCopy}
          className="w-full flex items-center gap-2 px-4 py-3 rounded-2xl bg-rbr-cream-cool border border-rbr-gold-soft/60 hover:border-rbr-gold text-left transition-colors group mb-5"
          data-testid="share-link-preview"
        >
          <span className="text-[11px] flex-1 truncate text-rbr-blue-deep font-mono">
            {link}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-rbr-gold-deep font-semibold flex-shrink-0">
            {copied ? 'Copied' : 'Tap to copy'}
          </span>
        </button>

        {/* Share buttons grid */}
        <div className="grid grid-cols-5 gap-2">
          {buttons.map((b) => {
            const Icon = b.icon;
            return (
              <button
                key={b.key}
                onClick={b.onClick}
                disabled={b.disabled}
                data-testid={`share-btn-${b.key}`}
                className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-rbr-cream-cool border border-rbr-gold-soft/40 hover:-translate-y-0.5 hover:shadow-rbr-lift disabled:opacity-60 disabled:cursor-wait transition-all"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${b.tint}22`, boxShadow: `inset 0 0 0 1px ${b.tint}55` }}
                >
                  <Icon className="w-4 h-4" style={{ color: b.tint }} />
                </div>
                <span className="text-[9px] uppercase tracking-widest font-semibold text-rbr-blue-deep text-center leading-tight">
                  {b.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AffirmationShareMenu;
